import {useProjectHasTraceServerData} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/pages/wfReactInterface/context';
import {useGetTraceServerClientContext} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/pages/wfReactInterface/traceServerClientContext';
import type {Query} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/pages/wfReactInterface/traceServerClientInterface/query';
import {projectIdFromParts} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/pages/wfReactInterface/tsDataModelHooks';
import {useContext, useEffect, useState} from 'react';

import {WANDB_EVAL_TABLE_ATTRIBUTE_PATH} from '../components/PanelEvaluationTables/common';
import {RunQueryContext} from '../state/runs/context';

/**
 * Excludes `Evaluation.evaluate` calls authored by the wandb SDK `EvalTable`
 * logger (marked `_weave_eval_meta.wandb_eval_table: true`). Those belong in the
 * Eval Tables panel, not the Evaluations (Weave Trace) panel — so we filter them
 * out of that panel and out of the signal that decides whether to show it.
 */
export const EXCLUDE_WANDB_EVAL_TABLE_QUERY: Query = {
  $expr: {
    $or: [
      {$eq: [{$getField: WANDB_EVAL_TABLE_ATTRIBUTE_PATH}, {$literal: null}]},
      {$eq: [{$getField: WANDB_EVAL_TABLE_ATTRIBUTE_PATH}, {$literal: ''}]},
      {
        $not: [
          {
            $eq: [
              {$getField: WANDB_EVAL_TABLE_ATTRIBUTE_PATH},
              {$literal: true},
            ],
          },
        ],
      },
    ],
  },
};

/**
 * Hook that checks if weave trace panel should be included based on:
 * 1. Feature flag (weave_panels_in_workspace)
 * 2. Project has trace server data
 * 3. Project has calls with wb_run_id (expensive check)
 */
export const useCheckWeaveSignal = (): boolean => {
  const {entityName, projectName} = useContext(RunQueryContext);

  // by default we don't include the weave trace panel
  const [shouldIncludeWeavePanel, setShouldIncludeWeavePanel] =
    useState<boolean>(false);

  const projectHasTraceData = useProjectHasTraceServerData(
    entityName ?? '',
    projectName ?? ''
  );
  const getTsClient = useGetTraceServerClientContext();

  useEffect(() => {
    // Early exit if feature flag is loading or disabled
    if (projectHasTraceData.loading) {
      return;
    }

    // If the project has no trace server data, we can't show the weave signal
    if (!projectHasTraceData.result) {
      return;
    }

    (async () => {
      const res = await getTsClient().callsQueryStats({
        project_id: projectIdFromParts({
          entity: entityName ?? '',
          project: projectName ?? '',
        }),
        // Roots only: eval-table children carry `wb_run_id` but not the marker, so
        // without this an eval-table-only project would still show the panel. Safe:
        // the panel is roots-only by default (full traces live in the Traces page).
        filter: {trace_roots_only: true},
        // set limit to 1 to avoid unnecessary data reads
        limit: 1,
        query: {
          $expr: {
            $and: [
              // Filter for non-null wb_run_id
              {
                $not: [
                  {
                    $eq: [{$getField: 'wb_run_id'}, {$literal: null}],
                  },
                ],
              },
              // ...and isn't an eval-table-marked eval (those belong in the
              // Eval Tables panel), so the panel hides when the only
              // run-associated traces are eval tables.
              EXCLUDE_WANDB_EVAL_TABLE_QUERY.$expr,
            ],
          },
        },
      });

      setShouldIncludeWeavePanel(res.count > 0);
    })();
  }, [
    entityName,
    getTsClient,
    projectName,
    projectHasTraceData.loading,
    projectHasTraceData.result,
  ]);

  return shouldIncludeWeavePanel;
};
